(() => {
let externalPlayerId = 0;
class DDUGCCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-dd-carousel-track]');
    this.items = Array.from(this.querySelectorAll('[data-dd-carousel-item]'));
    if (!this.track || this.items.length === 0) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true, signal });
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
    this.addEventListener('dd:carousel-resume', () => this.releaseSelectedMedia(), { signal });
    document.addEventListener('visibilitychange', () => this.onVisibilityChange(), { signal });
    window.addEventListener('message', (event) => this.onExternalMessage(event), { signal });

    this.resizeObserver = new ResizeObserver(() => this.updateActive());
    this.resizeObserver.observe(this.track);

    // Warm still images on approach without hydrating any video sources.
    if ('IntersectionObserver' in window) {
      this.posterObserver = new IntersectionObserver(() => this.warmPosters(), {
        rootMargin: '400px 0px',
        threshold: 0
      });
      this.posterObserver.observe(this);
    }
    this.posterMutationObserver = new MutationObserver(() => this.queueUpdate());
    this.posterMutationObserver.observe(this.track, { childList: true });

    // Start closed: native autoplay must not race the first observer delivery.
    this.visible = false;
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.visible = entry?.isIntersecting ?? false;
          if (this.visible) this.updateActive();
          else this.pauseAll();
        },
        { threshold: 0.2 }
      );
      this.intersectionObserver.observe(this);
    } else {
      // Older browsers use the same viewport gates, never eager page-wide loading.
      window.addEventListener('scroll', () => this.queueUpdate(), { passive: true, capture: true, signal });
      window.addEventListener('resize', () => this.queueUpdate(), { passive: true, signal });
    }

    this.activationItem = null;
    this.nativeControls = this.dataset.nativeControls === 'true';
    this.nativeOwnerItem = null;
    this.nativeOwnerScroll = null;
    this.nativeOwnedPause = false;
    this.externalPlayers = new Map();
    this.addEventListener('load', (event) => {
      if (!event.target.matches?.('[data-dd-ugc-native-poster]')) return;
      const video = event.target.closest('[data-dd-ugc-card]')?.querySelector('video');
      if (video && !video.hasAttribute('data-dd-ugc-deferred')) this.syncVideoPoster(video);
    }, { capture: true, signal });
    this.addEventListener('loadeddata', (event) => {
      if (event.target.matches?.('video')) this.syncVideoPoster(event.target);
    }, { capture: true, signal });
    this.addEventListener('playing', (event) => {
      const video = event.target;
      if (!video.matches?.('video')) return;
      const revealVideo = () => {
        if (!video.isConnected || video.readyState < 2) return;
        video.dataset.ddUgcFrameReady = 'true';
        this.syncVideoPoster(video);
      };
      if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(revealVideo);
      else revealVideo();
    }, { capture: true, signal });
    for (const name of ['emptied', 'error']) this.addEventListener(name, (event) => {
      const video = event.target;
      if (!video.matches?.('video')) return;
      delete video.dataset.ddUgcFrameReady;
      const poster = video.closest('[data-dd-ugc-card]')?.querySelector('[data-dd-ugc-native-poster]');
      if (poster) poster.hidden = false;
    }, { capture: true, signal });
    this.querySelectorAll('iframe[data-src]').forEach((iframe) => this.connectExternal(iframe, signal));
    if (this.nativeControls) {
      this.querySelectorAll('[data-dd-native-controls="true"] video').forEach((video) => {
        video.controls = false;
        video.tabIndex = 0;
      });
      this.addEventListener('pointerdown', (event) => {
        const video = event.target.closest?.('[data-dd-native-controls="true"] video');
        if (video && this.contains(video)) {
          if (event.pointerType === 'touch') {
            this.nativeTouchGesture = { video, id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
            return;
          }
          video.controls = true;
          this.beginNativeInteraction(video);
        }
      }, { capture: true, signal });
      this.addEventListener('pointermove', (event) => {
        const gesture = this.nativeTouchGesture;
        if (!gesture || gesture.id !== event.pointerId) return;
        if (Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 10) {
          gesture.moved = true;
          this.querySelectorAll('video').forEach((video) => { video.controls = false; });
        }
      }, { passive: true, signal });
      this.addEventListener('pointercancel', () => { this.nativeTouchGesture = null; }, { signal });
      this.addEventListener('pointerover', (event) => {
        const video = event.pointerType === 'mouse' ? event.target.closest?.('[data-dd-native-controls="true"] video') : null;
        if (video && this.contains(video)) {
          video.controls = true;
          this.syncVideoPoster(video);
        }
      }, { signal });
      this.addEventListener('pointerout', (event) => {
        const video = event.pointerType === 'mouse' ? event.target.closest?.('[data-dd-native-controls="true"] video') : null;
        if (video && !video.contains(event.relatedTarget) && !video.matches(':focus')) video.controls = false;
      }, { signal });
      this.addEventListener('focusin', (event) => {
        if (this.nativeTouchGesture) return;
        const video = event.target.closest?.('[data-dd-native-controls="true"] video');
        if (video && this.contains(video)) {
          video.controls = true;
          this.beginNativeInteraction(video);
        }
      }, { signal });
      this.addEventListener('focusout', (event) => {
        const video = event.target.closest?.('[data-dd-native-controls="true"] video');
        if (video) requestAnimationFrame(() => { if (!video.matches(':focus, :hover')) video.controls = false; });
      }, { signal });
      this.addEventListener('play', (event) => this.onNativePlay(event), { capture: true, signal });
      this.addEventListener('pause', (event) => this.onNativePause(event), { capture: true, signal });
    } else this.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      video.addEventListener('playing', () => {
        const card = video.closest('[data-dd-ugc-card]');
        this.setPlaying(card, true);
        if (video.dataset.ddDeliberatePlay === 'true') {
          delete video.dataset.ddDeliberatePlay;
          this.persistCarouselPause(video);
        }
        this.hideControlsOnPlayback(card);
      }, { signal });
      for (const name of ['pause', 'ended', 'error']) video.addEventListener(name, () => this.setPlaying(video.closest('[data-dd-ugc-card]'), false), { signal });
    });
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionQuery.addEventListener('change', () => {
      if (this.motionQuery.matches) this.pauseAll();
      else this.updateActive();
    }, { signal });
    this.updateActive();
  }

  disconnectedCallback() {
    this.pauseAll();
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.posterObserver?.disconnect();
    this.posterMutationObserver?.disconnect();
    this.externalPlayers?.forEach((state) => this.resetExternal(state));
    this.externalPlayers?.clear();
    if (this.updateFrame) window.cancelAnimationFrame(this.updateFrame);
    this.updateFrame = null;
    this.updateQueued = false;
  }

  get autoplay() {
    return this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  queueUpdate() {
    if (this.updateQueued) return;
    this.updateQueued = true;
    this.updateFrame = window.requestAnimationFrame(() => {
      this.updateFrame = null;
      this.updateQueued = false;
      if (!this.isConnected) return;
      this.updateActive();
    });
  }

  warmPosters() {
    const trackBox = this.track.getBoundingClientRect();
    if (trackBox.bottom < -400 || trackBox.top > window.innerHeight + 400) return;
    const left = Math.max(0, trackBox.left);
    const right = Math.min(window.innerWidth, trackBox.right);
    this.track.querySelectorAll('[data-dd-ugc-native-poster]').forEach((poster) => {
      if (poster.loading === 'eager') return;
      const box = poster.getBoundingClientRect();
      const lookAhead = box.width + 32;
      if (box.width <= 0 || box.right < left - lookAhead || box.left > right + lookAhead) return;
      poster.decoding = 'async';
      poster.loading = 'eager';
    });
  }

  syncVideoPoster(video) {
    const poster = video.closest('[data-dd-ugc-card]')?.querySelector('[data-dd-ugc-native-poster]');
    if (!poster?.complete || poster.naturalWidth === 0) return;
    // Reuse the browser-selected responsive image, never the video_tag poster URL.
    const source = poster.currentSrc || poster.src;
    if (video.poster !== source) video.poster = source;
    if (video.dataset.ddUgcFrameReady === 'true' && video.readyState >= 2) poster.hidden = true;
  }

  hydrateVideo(video) {
    if (!video.hasAttribute('data-dd-ugc-deferred')) return;
    this.syncVideoPoster(video);
    // The video_tag fallback and fixed-size poster stay inert; the responsive image supplies the poster.
    video.querySelectorAll('source[data-dd-ugc-src]').forEach((source) => {
      source.src = source.dataset.ddUgcSrc;
      delete source.dataset.ddUgcSrc;
    });
    if (video.dataset.ddUgcSrc) {
      video.src = video.dataset.ddUgcSrc;
      delete video.dataset.ddUgcSrc;
    }
    video.removeAttribute('data-dd-ugc-deferred');
    video.load();
  }

  updateActive() {
    this.warmPosters();
    if (!this.intersectionObserver) {
      const box = this.getBoundingClientRect();
      this.visible = box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < innerHeight;
      if (!this.visible) this.pauseAll();
    }
    const trackBox = this.track.getBoundingClientRect();
    const centre = trackBox.left + trackBox.width / 2;
    const candidates = this.nativeControls ? this.playbackItems : this.items;
    let activeItem = candidates[0];
    let activeDistance = Number.POSITIVE_INFINITY;

    candidates.forEach((item) => {
      const box = item.getBoundingClientRect();
      const distance = Math.abs(box.left + box.width / 2 - centre);
      if (distance < activeDistance) {
        activeDistance = distance;
        activeItem = item;
      }
    });

    if (this.dataset.autoplayFirst === 'true' && window.matchMedia('(min-width: 990px)').matches) {
      const mediaItems = candidates.filter((item) => item.querySelector('video, iframe'));
      const carouselBox = this.getBoundingClientRect();
      const firstVisibleMedia = window.matchMedia('(min-width: 990px)').matches
        ? mediaItems.find((item) => {
            const box = item.getBoundingClientRect();
            return box.left >= Math.max(0, trackBox.left, carouselBox.left) - 1 &&
              box.right <= Math.min(window.innerWidth, trackBox.right, carouselBox.right) + 1;
          })
        : null;
      activeItem = firstVisibleMedia || mediaItems[0];
    }

    if (this.nativeOwnerItem) {
      const carousel = this.track.closest('dd-continuous-carousel');
      const moved = Math.abs((carousel?.track.scrollLeft ?? 0) - (this.nativeOwnerScroll ?? 0)) > 10;
      if (moved && activeItem !== this.nativeOwnerItem) this.releaseNativeOwner(true);
      else activeItem = this.nativeOwnerItem;
    }
    if (!this.nativeOwnerItem && this.activationItem && this.items.includes(this.activationItem)) activeItem = this.activationItem;

    this.playbackItems.forEach((item) => {
      const isActive = item === activeItem;
      item.toggleAttribute('data-active', isActive);
      if (!isActive) this.pause(item);
    });

    this.activeItem = activeItem;
    const activeBox = activeItem?.getBoundingClientRect();
    const activeVisible = activeBox && activeBox.right > trackBox.left + 20 && activeBox.left < trackBox.right - 20;
    if (!activeVisible) this.pause(activeItem);
    if (this.autoplay && !this.nativeOwnerItem && !this.activationItem && this.visible && activeVisible && !document.hidden) this.play(activeItem);
  }

  onClick(event) {
    const sound = event.target.closest('[data-dd-ugc-sound]');
    if (sound && this.contains(sound)) {
      const card = sound.closest('[data-dd-ugc-card]');
      const video = card?.querySelector('video');
      const iframe = card?.querySelector('iframe[src]');
      const unmuted = card?.dataset.ddUserUnmuted !== 'true';
      if (!video && !iframe) return;
      card.dataset.ddUserUnmuted = String(unmuted);
      if (video) video.muted = !unmuted;
      if (iframe) this.setExternalMuted(this.externalPlayers.get(iframe), !unmuted);
      this.syncSound(card, !unmuted);
      this.revealControls(card);
      this.releaseTouchFocus(event, sound);
      return;
    }
    const button = event.target.closest('[data-dd-ugc-toggle]');
    if (!button || !this.contains(button)) return;

    const item = button.closest('[data-dd-carousel-item]');
    const card = button.closest('[data-dd-ugc-card]');
    if (!item || !card) return;

    if (item !== this.activeItem) {
      this.beginDeliberatePlayback(item);
      item.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      this.items.forEach((candidate) => {
        if (candidate !== item) this.pause(candidate);
      });
      this.activeItem = item;
      this.items.forEach((candidate) => candidate.toggleAttribute('data-active', candidate === item));
      this.play(item, true);
      this.releaseTouchFocus(event, button);
      return;
    }

    if (card.hasAttribute('data-playing')) {
      this.pause(item);
      this.revealControls(card);
    } else {
      this.beginDeliberatePlayback(item);
      this.play(item, true);
    }
    this.releaseTouchFocus(event, button);
  }

  onPointerUp(event) {
    if (event.pointerType !== 'touch') return;
    if (this.nativeControls) {
      const gesture = this.nativeTouchGesture;
      this.nativeTouchGesture = null;
      if (gesture && gesture.id === event.pointerId && !gesture.moved &&
          Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) <= 10) {
        gesture.video.controls = true;
        this.beginNativeInteraction(gesture.video);
      }
      return;
    }
    const control = event.target.closest('[data-dd-ugc-toggle], [data-dd-ugc-sound]');
    if (control && this.contains(control)) {
      window.setTimeout(() => {
        if (this.isConnected && document.activeElement === control) control.blur();
      }, 0);
      return;
    }
    if (event.target.closest('button, a')) return;
    const card = event.target.closest('[data-dd-ugc-card]');
    if (card) this.revealControls(card);
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get playbackItems() {
    return this.nativeControls
      ? Array.from(this.querySelectorAll('[data-dd-carousel-item], [data-dd-loop-clone]'))
      : this.items;
  }

  beginNativeInteraction(video) {
    this.hydrateVideo(video);
    const item = video.closest('[data-dd-carousel-item], [data-dd-loop-clone]');
    const carousel = video.closest('dd-continuous-carousel');
    if (!item || !carousel) return;
    if (!this.nativeOwnerItem) this.nativeOwnedPause = !carousel.explicitlyPaused;
    this.nativeOwnerItem = item;
    this.nativeOwnerScroll = carousel.track.scrollLeft;
    carousel.setPersistentPause?.(true);
    this.playbackItems.forEach((candidate) => {
      if (candidate !== item) this.pause(candidate);
      candidate.toggleAttribute('data-active', candidate === item);
    });
    this.activeItem = item;
  }

  onNativePlay(event) {
    const video = event.target.closest?.('[data-dd-native-controls="true"] video');
    if (!video || !this.nativeControls) return;
    this.setPlaying(video.closest('[data-dd-ugc-card]'), true);
  }

  onNativePause(event) {
    const video = event.target.closest?.('[data-dd-native-controls="true"] video');
    if (!video || !this.nativeControls) return;
    this.setPlaying(video.closest('[data-dd-ugc-card]'), false);
  }

  releaseNativeOwner(navigated = false) {
    const item = this.nativeOwnerItem;
    if (!item) return;
    this.nativeOwnerItem = null;
    this.nativeOwnerScroll = null;
    if (navigated) this.pause(item);
    const carousel = this.track.closest('dd-continuous-carousel');
    const releasePause = this.nativeOwnedPause;
    this.nativeOwnedPause = false;
    if (releasePause && carousel?.explicitlyPaused) carousel.setPersistentPause(false);
  }

  beginDeliberatePlayback(item) {
    this.activationItem = item;
    item.closest('dd-continuous-carousel')?.pause?.('media-activation');
  }

  releaseTouchFocus(event, control) {
    if (event.pointerType !== 'touch') return;
    window.requestAnimationFrame(() => {
      if (this.isConnected && document.activeElement === control) control.blur();
    });
  }

  play(item, userInitiated = false) {
    if (!item || (!this.visible && !userInitiated)) return;
    const card = item.querySelector('[data-dd-ugc-card]');
    if (!card || card.classList.contains('dd-ugc-card--image')) return;

    this.playbackItems.forEach((candidate) => {
      if (candidate !== item) this.pause(candidate);
    });

    const video = card.querySelector('video');
    const iframe = card.querySelector('iframe[data-src]');
    if (video) {
      this.hydrateVideo(video);
      if (!userInitiated || card.dataset.ddUserUnmuted !== 'true') video.muted = true;
      if (userInitiated) video.dataset.ddDeliberatePlay = 'true';
      const promise = video.play();
      if (promise) promise.then(() => {
        if (userInitiated && video.dataset.ddDeliberatePlay === 'true' && !video.paused) {
          delete video.dataset.ddDeliberatePlay;
          this.setPlaying(card, true);
          this.persistCarouselPause(video);
          this.hideControlsOnPlayback(card);
        }
      }).catch(() => {
        this.cancelActivation(video);
        this.setPlaying(card, false);
      });
    } else if (iframe) {
      const state = this.externalPlayers.get(iframe);
      if (state) this.playExternal(state, userInitiated);
      else if (userInitiated) this.cancelActivation(iframe);
    }
  }

  pause(item) {
    const card = item?.querySelector('[data-dd-ugc-card]');
    if (!card) return;
    card.querySelector('video')?.pause();
    const iframe = card.querySelector('iframe[src]');
    if (iframe) {
      const state = this.externalPlayers.get(iframe);
      if (state) {
        this.postPlayback(iframe, 'pause');
        this.resetExternal(state);
      }
    }
    this.setPlaying(card, false);
  }

  pauseAll() {
    this.playbackItems.forEach((item) => this.pause(item));
  }

  setPlaying(card, playing) {
    if (!card) return;
    card.toggleAttribute('data-playing', playing);
    if (!playing) card.removeAttribute('data-dd-controls-visible');
    const button = card.querySelector('[data-dd-ugc-toggle]');
    if (!button) return;
    button.setAttribute('aria-pressed', String(playing));
    button.ariaLabel = playing ? 'Pause video' : 'Play video';
    const video = card.querySelector('video');
    if (video) this.syncSound(card, video.muted);
  }

  syncSound(card, muted) {
    const button = card?.querySelector('[data-dd-ugc-sound]');
    if (!button) return;
    button.setAttribute('aria-pressed', String(!muted));
    button.ariaLabel = muted ? 'Unmute video' : 'Mute video';
    button.textContent = muted ? 'Sound off' : 'Sound on';
  }

  revealControls(card) {
    card.setAttribute('data-dd-controls-visible', '');
    this.hideControlsLater(card);
  }

  hideControlsLater(card) {
    if (!card) return;
    clearTimeout(card.ddControlsTimer);
    card.ddControlsTimer = setTimeout(() => {
      if (!card.matches(':focus-within')) card.removeAttribute('data-dd-controls-visible');
    }, 2200);
  }

  hideControlsOnPlayback(card) {
    if (!card) return;
    clearTimeout(card.ddControlsTimer);
    card.removeAttribute('data-dd-controls-visible');
  }

  persistCarouselPause(media) {
    const carousel = media?.closest('dd-continuous-carousel');
    this.releaseActivationPause(media);
    carousel?.setPersistentPause?.(true);
    media?.dispatchEvent(new CustomEvent('dd:media-play', { bubbles: true }));
  }

  releaseActivationPause(media) {
    const item = media?.closest('[data-dd-carousel-item]');
    if (!item || this.activationItem !== item) return;
    item.closest('dd-continuous-carousel')?.resume?.('media-activation');
  }

  cancelActivation(media) {
    const item = media?.closest('[data-dd-carousel-item]');
    if (!item || this.activationItem !== item) return;
    this.activationItem = null;
    item.closest('dd-continuous-carousel')?.resume?.('media-activation');
    this.queueUpdate();
  }

  releaseSelectedMedia() {
    this.releaseNativeOwner();
    if (!this.activationItem) return;
    this.activationItem = null;
    this.queueUpdate();
  }

  connectExternal(iframe, signal) {
    const provider = iframe.dataset.src.includes('youtube') ? 'youtube' : 'vimeo';
    const url = new URL(iframe.dataset.src, window.location.href);
    const allowed = provider === 'youtube'
      ? ['https://www.youtube-nocookie.com', 'https://www.youtube.com'].includes(url.origin)
      : url.origin === 'https://player.vimeo.com';
    if (!allowed) return;
    const state = { iframe, card: iframe.closest('[data-dd-ugc-card]'), provider, origin: url.origin, ready: false, requested: false };
    this.externalPlayers.set(iframe, state);
    iframe.addEventListener('load', () => {
      state.loaded = iframe.hasAttribute('src');
      if (state.requested) this.probeExternal(state);
    }, { signal });
    iframe.addEventListener('error', () => this.resetExternal(state), { signal });
  }

  playExternal(state, deliberate) {
    if (!state) return;
    if (state.requested) {
      if (deliberate) state.deliberate = true;
      return;
    }
    state.requested = true;
    state.deliberate = deliberate;
    state.card.setAttribute('data-dd-play-requested', '');
    if (!state.iframe.hasAttribute('src')) {
      state.id = `dd-ugc-${++externalPlayerId}`;
      state.commandId = state.id;
      state.ready = state.loaded = false;
      const url = new URL(state.iframe.dataset.src, window.location.href);
      url.searchParams.set('autoplay', '0');
      url.searchParams.set(state.provider === 'youtube' ? 'mute' : 'muted', '1');
      url.searchParams.set('controls', '0');
      if (state.provider === 'youtube') {
        url.searchParams.set('origin', window.location.origin);
        url.searchParams.set('widgetid', state.id);
      } else {
        url.searchParams.set('title', '0');
        url.searchParams.set('byline', '0');
        url.searchParams.set('portrait', '0');
      }
      state.iframe.src = url.href;
    }
    state.iframe.hidden = false;
    state.iframe.setAttribute('data-dd-provider-starting', '');
    state.iframe.setAttribute('aria-hidden', 'true');
    state.iframe.tabIndex = -1;
    clearTimeout(state.timeout);
    state.timeout = setTimeout(() => this.resetExternal(state), 12000);
    if (state.ready) this.postPlayback(state.iframe, 'play');
    else {
      this.probeExternal(state);
      clearInterval(state.handshake);
      state.handshake = setInterval(() => this.probeExternal(state), 250);
    }
  }

  probeExternal(state) {
    if (!state.loaded || state.ready || !state.requested) return;
    this.postExternal(state, state.provider === 'youtube' ? { event: 'listening' } : { method: 'ping' });
  }

  onExternalMessage(event) {
    const state = [...this.externalPlayers.values()].find((entry) => entry.iframe.contentWindow === event.source && entry.origin === event.origin);
    if (!state || !state.loaded || !state.iframe.hasAttribute('src')) return;
    let data = event.data;
    try { if (typeof data === 'string') data = JSON.parse(data); } catch { return; }
    if (!data || typeof data !== 'object') return;
    const youtube = state.provider === 'youtube';
    if (data.event === 'onError' || data.event === 'onAutoplayBlocked' || data.event === 'error') return this.resetExternal(state);
    const ready = youtube ? data.event === 'onReady' && data.id != null : data.event === 'ready' || data.method === 'ping';
    if (youtube && ready && !state.ready) state.commandId = data.id;
    if (ready && !state.ready) {
      state.ready = true;
      clearInterval(state.handshake);
      const events = youtube ? ['onStateChange', 'onError', 'onAutoplayBlocked'] : ['playing', 'pause', 'ended', 'error'];
      events.forEach((name) => this.postExternal(state, youtube ? { event: 'command', func: 'addEventListener', args: [name] } : { method: 'addEventListener', value: name }));
      if (state.requested) this.postPlayback(state.iframe, 'play');
    }
    const playerState = youtube ? (data.event === 'onStateChange' ? data.info : data.event === 'infoDelivery' ? data.info?.playerState : undefined) : undefined;
    const playing = youtube ? playerState === 1 : data.event === 'playing';
    const stopped = youtube ? playerState === 0 || playerState === 2 : data.event === 'pause' || data.event === 'ended';
    if (playing && state.requested) {
      clearTimeout(state.timeout);
      state.card.removeAttribute('data-dd-play-requested');
      state.iframe.removeAttribute('data-dd-provider-starting');
      state.iframe.removeAttribute('aria-hidden');
      state.iframe.removeAttribute('tabindex');
      state.card.querySelector('.dd-ugc-card__poster')?.setAttribute('hidden', '');
      this.setPlaying(state.card, true);
      if (state.deliberate) {
        state.deliberate = false;
        this.persistCarouselPause(state.iframe);
      }
      this.hideControlsOnPlayback(state.card);
    } else if (stopped) this.resetExternal(state);
  }

  resetExternal(state) {
    clearTimeout(state.timeout);
    clearInterval(state.handshake);
    state.requested = state.ready = state.loaded = false;
    state.card.removeAttribute('data-dd-play-requested');
    state.iframe.removeAttribute('data-dd-provider-starting');
    state.iframe.removeAttribute('aria-hidden');
    state.iframe.removeAttribute('tabindex');
    state.iframe.removeAttribute('src');
    state.iframe.hidden = true;
    state.card.querySelector('.dd-ugc-card__poster')?.removeAttribute('hidden');
    this.cancelActivation(state.iframe);
    this.setPlaying(state.card, false);
  }

  postExternal(state, message) {
    if (!state?.iframe.hasAttribute('src')) return;
    if (state.provider === 'youtube') message = { ...message, id: state.commandId ?? state.id, channel: 'widget' };
    state.iframe.contentWindow?.postMessage(JSON.stringify(message), state.origin);
  }

  setExternalMuted(state, muted) {
    if (!state?.ready) return;
    this.postExternal(state, state.provider === 'youtube'
      ? { event: 'command', func: muted ? 'mute' : 'unMute', args: [] }
      : { method: 'setVolume', value: muted ? 0 : 1 });
  }

  postPlayback(iframe, action) {
    const state = this.externalPlayers?.get(iframe);
    if (!state?.ready) return;
    if (action === 'play' && state.card.dataset.ddUserUnmuted !== 'true') this.setExternalMuted(state, true);
    this.postExternal(state, state.provider === 'youtube'
      ? { event: 'command', func: `${action}Video`, args: [] }
      : { method: action });
  }

  onVisibilityChange() {
    if (document.hidden) this.pauseAll();
    else this.updateActive();
  }
}

if (!customElements.get('dd-ugc-carousel')) customElements.define('dd-ugc-carousel', DDUGCCarousel);
})();
